import React from 'react';
import { useIntl } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';
import { SrOnly } from './UserStatusBadge';

/**
 * Plassholderen som står mens kravtreet hentes.
 *
 * `validate-me` bruker noen sekunder — den slår opp i flere kilderegistre før
 * motoren kjøres — og en spinner midt på en tom flate sier bare «vent». Denne
 * plassholderen sier hva det er som kommer: en overskrift, noen krav rett på
 * flaten, og alternativboksene ved siden av hverandre. Formene er hentet fra de
 * ekte komponentene (`UserRequirementsView` og `UserNodeList`) — samme padding,
 * samme rammer, samme rutenett — slik at siden ikke hopper når innholdet
 * kommer.
 *
 * Antallet rader og bokser er et **gjett**, ikke et løfte: kravtreet er ikke
 * hentet ennå, og skeletonen kan umulig vite hvor mange krav sertifikatet har.
 * Tre og tre er det vanligste, og er valgt fordi det ligner nok til å bære
 * blikket uten å love noe.
 *
 * Tilgjengelighet: hele flaten er én `status`-region med `aria-busy`, og alt det
 * visuelle er `aria-hidden`. Det eneste en skjermleser hører er den visuelt
 * skjulte beskjeden om at kravene hentes — ingen tomme streker leses opp som om
 * de var ekte krav.
 */
export const UserRequirementsSkeleton: React.FC = () => {
  const intl = useIntl();

  return (
    <Skeleton role="status" aria-busy="true" aria-live="polite" data-testid="certreq-skeleton">
      <SrOnly>{intl.formatMessage({ id: 'certreq.userview.loading' })}</SrOnly>

      <Shapes aria-hidden="true">
        <HeadingBar />

        {/* Basiskravene — kortformen fra `UserNodeList` med variant `card`. */}
        <List>
          <RowCard>
            <Dot />
            <TitleBar style={{ width: '72%' }} />
          </RowCard>
          <RowCard>
            <Dot />
            <TitleBar style={{ width: '58%' }} />
          </RowCard>
          <RowCard>
            <Dot />
            <TitleBar style={{ width: '81%' }} />
          </RowCard>
        </List>

        {/* Overskrifta over alternativboksene, med samme luft rundt seg. */}
        <AlternativesHeader>
          <SubHeadingBar />
        </AlternativesHeader>

        {/* Alternativene side om side — samme rutenett som de ekte boksene. */}
        <Grid>
          <BoxCard>
            <TitleBar style={{ width: '64%' }} />
            <BareList>
              <BareRow>
                <TitleBar style={{ width: '86%' }} />
              </BareRow>
              <BareRow>
                <TitleBar style={{ width: '70%' }} />
              </BareRow>
            </BareList>
          </BoxCard>
          <BoxCard>
            <TitleBar style={{ width: '55%' }} />
            <BareList>
              <BareRow>
                <TitleBar style={{ width: '78%' }} />
              </BareRow>
              <BareRow>
                <TitleBar style={{ width: '90%' }} />
              </BareRow>
            </BareList>
          </BoxCard>
          <BoxCard>
            <TitleBar style={{ width: '70%' }} />
            <BareList>
              <BareRow>
                <TitleBar style={{ width: '82%' }} />
              </BareRow>
              <BareRow>
                <TitleBar style={{ width: '66%' }} />
              </BareRow>
            </BareList>
          </BoxCard>
        </Grid>
      </Shapes>
    </Skeleton>
  );
};

const Skeleton = styled.div`
  width: 100%;
`;

/** Mirrorer `View` i `UserRequirementsView` — samme kolonne og samme luft. */
const Shapes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  width: 100%;
`;

/**
 * Skimmeret som sier at flaten venter på noe.
 *
 * Det er en lysstripe som glir over en dus grunnflate — diskré med vilje: den
 * skal antyde bevegelse i utkanten av synsfeltet, ikke trekke blikket til seg.
 */
const shimmer = keyframes`
  from {
    background-position: 150% 0;
  }

  to {
    background-position: -50% 0;
  }
`;

/**
 * Grunnformen for alle strekene.
 *
 * Fargene er svarttoner med gjennomsiktighet i stedet for temafarger, slik at
 * strekene ligger like riktig på hvit kortflate som på sidens egen bakgrunn.
 *
 * `prefers-reduced-motion` slår av både bevegelsen og lysstripa. Da står
 * strekene stille som flate felt — plassholderen sier fortsatt hva som kommer,
 * og `aria-busy` sier fortsatt at det pågår.
 */
const bar = css`
  display: block;
  border-radius: 3px;
  background-color: rgb(0 0 0 / 8%);
  background-image: linear-gradient(
    90deg,
    rgb(255 255 255 / 0%) 0%,
    rgb(255 255 255 / 55%) 50%,
    rgb(255 255 255 / 0%) 100%
  );
  background-repeat: no-repeat;
  background-size: 60% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    background-image: none;
    animation: none;
  }
`;

/**
 * Overskrifta. Høyden er `Heading`s egen linje — 24 px tekst med linjehøyde
 * 1.3 — slik at teksten lander nøyaktig der streken stod.
 */
const HeadingBar = styled.span`
  ${bar}

  width: 62%;
  max-width: 46rem;
  height: 31px;
`;

/** Alternativoverskrifta: 18 px tekst med linjehøyde 1.3. */
const SubHeadingBar = styled.span`
  ${bar}

  width: 38%;
  max-width: 30rem;
  height: 23px;
`;

/** Radteksten: 16 px med linjehøyde 1.35, samme som `Title` i `UserNodeList`. */
const TitleBar = styled.span`
  ${bar}

  height: 22px;
`;

/**
 * Statusansiktet står i sin egen kolonne, og har omtrent denne flaten.
 *
 * Sirkelen er like stor som ansiktet (3rem), men **legger ikke beslag på 3rem
 * høyde**: de negative margene krymper cellen ned til `Emoji`s egen 1.35rem og
 * lar sirkelen flyte utenfor, akkurat slik den ekte emoji-glyffen gjør. Uten
 * det ville plassholderkortet vært over 25 px høyere enn kortet som kommer, og
 * siden ville hoppet.
 *
 * `+ 5px` på toppen og `-4px` til venstre er `Emoji`s egne optiske justeringer,
 * speilet her slik at sirkelen står nøyaktig der ansiktet lander.
 */
const Dot = styled.span`
  ${bar}

  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  margin-top: calc((1.35rem - 3rem) / 2 + 5px);
  margin-bottom: calc((1.35rem - 3rem) / 2);
  margin-left: -4px;
`;

/** Samme avstand mellom kortene som i `UserNodeList` med variant `card`. */
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

/**
 * Kravene inne i en alternativboks. De står tettere enn kortene ute på flaten —
 * `bare`-variantens egen avstand i `UserNodeList` — og har derfor sin egen
 * liste, i stedet for å arve boksens luft mellom hode og innhold.
 */
const BareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

/**
 * Kravkortet, med `Node`s mål: statuskolonnen på 4.2rem, kortets padding og
 * rammen rundt. Hopper siden når det ekte kortet kommer, er det her det er
 * kommet ut av takt.
 */
const RowCard = styled.div`
  display: grid;
  grid-template-columns: 4.2rem minmax(0, 1fr);
  align-items: start;
  padding: 1.45rem 1.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: ${({ theme }) => theme.colors.background.white};
`;

/** Luften rundt alternativoverskrifta, hentet fra `AlternativesHeader`. */
const AlternativesHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0 0.2rem;
  margin-top: 1.2rem;
`;

/** Rutenettet er `Grid` fra `UserRequirementsView`, variabler og alt. */
const Grid = styled.div`
  --box-gap: 0.9rem;
  --box-min: 19rem;
  --box-columns: 3;

  display: grid;
  gap: var(--box-gap);
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      max(
        var(--box-min),
        calc((100% - (var(--box-columns) - 1) * var(--box-gap)) / var(--box-columns))
      ),
      1fr
    )
  );
  align-items: stretch;
`;

/** Alternativboksen med `Box`-målene, inkludert den tykke venstrekanten. */
const BoxCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
  padding: 1.1rem 1.2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: ${({ theme }) => theme.colors.background.white};
`;

/** Kravraden inne i boksen — `bare`-variantens tettere padding. */
const BareRow = styled.div`
  padding: 1.2rem 1.4rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

export default UserRequirementsSkeleton;
